import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatDMY } from "@/lib/dmy";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Clock,
  Download,
  Printer,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw,
  AlertTriangle,
  Calendar,
  TrendingUp
} from "lucide-react";

const AgingSummaryReportPage = () => {
  usePageTitle("Aging Summary Report");
  const { token } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });
  const [activeTab, setActiveTab] = useState("partners");

  // Get date range from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const dateFrom = urlParams.get('from');
  const dateTo = urlParams.get('to');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/aging-summary${params.toString() ? `?${params}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [token, dateFrom, dateTo]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-amber-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-amber-600" />;
  };

  const filteredAndSortedPartners = useMemo(() => {
    if (!reportData?.partners) return [];
    
    let filtered = reportData.partners;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.client_code?.toLowerCase().includes(term) ||
        p.client_name?.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      
      if (sortConfig.key === 'client_code' || sortConfig.key === 'client_name') {
        return sortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [reportData, searchTerm, sortConfig]);

  const exportToCSV = () => {
    if (!reportData?.partners) return;
    
    const headers = ["Partner Code", "Partner Name", "0-30 Days", "31-60 Days", "61-90 Days", "90+ Days", "Total", "Orders"];
    const rows = filteredAndSortedPartners.map(p => [
      p.client_code,
      p.client_name,
      p["0_30"].toFixed(2),
      p["31_60"].toFixed(2),
      p["61_90"].toFixed(2),
      p["over_90"].toFixed(2),
      p.total.toFixed(2),
      p.order_count
    ]);
    
    // Add grand total
    rows.push([
      "GRAND TOTAL",
      "",
      reportData.grand_total["0_30"].toFixed(2),
      reportData.grand_total["31_60"].toFixed(2),
      reportData.grand_total["61_90"].toFixed(2),
      reportData.grand_total["over_90"].toFixed(2),
      reportData.grand_total.total.toFixed(2),
      ""
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aging_summary_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  const handlePrint = () => {
    window.print();
  };

  // Calculate percentages for progress bars
  const getAgingPercentage = (bucket) => {
    if (!reportData?.grand_total?.total || reportData.grand_total.total === 0) return 0;
    return Math.round((reportData.grand_total[bucket] / reportData.grand_total.total) * 100);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 print:p-2">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          table { font-size: 10px; }
          th, td { padding: 4px 6px !important; }
        }
      `}</style>

      {/* Header - Non-printable controls */}
      <div className="no-print sticky top-0 z-20 bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Aging Summary Report</h1>
                <p className="text-sm text-muted-foreground">
                  Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
                  {(dateFrom || dateTo) && (
                    <span className="ml-2">
                      | Period: {dateFrom || 'Start'} to {dateTo || 'Today'}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button size="sm" onClick={handlePrint} className="bg-amber-600 hover:bg-amber-700">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-[1600px] mx-auto p-6 print:p-2 print:max-w-none">
        {/* Print Header - Only visible when printing */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold text-center">Aging Summary Report</h1>
          <p className="text-center text-gray-600">
            Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
            {(dateFrom || dateTo) && ` | Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}`}
          </p>
        </div>

        {/* Aging Bucket Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-green-600 uppercase">0-30 Days</p>
              <p className="text-xl font-bold text-green-700 mt-1">
                ₹{(reportData?.grand_total?.["0_30"] || 0).toLocaleString()}
              </p>
              <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all" 
                  style={{ width: `${getAgingPercentage("0_30")}%` }}
                />
              </div>
              <p className="text-xs text-green-500 mt-1">{getAgingPercentage("0_30")}% of total</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-amber-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-amber-600 uppercase">31-60 Days</p>
              <p className="text-xl font-bold text-amber-700 mt-1">
                ₹{(reportData?.grand_total?.["31_60"] || 0).toLocaleString()}
              </p>
              <div className="mt-2 h-2 bg-amber-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 transition-all" 
                  style={{ width: `${getAgingPercentage("31_60")}%` }}
                />
              </div>
              <p className="text-xs text-amber-500 mt-1">{getAgingPercentage("31_60")}% of total</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-orange-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-orange-600 uppercase">61-90 Days</p>
              <p className="text-xl font-bold text-orange-700 mt-1">
                ₹{(reportData?.grand_total?.["61_90"] || 0).toLocaleString()}
              </p>
              <div className="mt-2 h-2 bg-orange-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-orange-500 transition-all" 
                  style={{ width: `${getAgingPercentage("61_90")}%` }}
                />
              </div>
              <p className="text-xs text-orange-500 mt-1">{getAgingPercentage("61_90")}% of total</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30 border-red-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-red-600 uppercase">90+ Days</p>
              <p className="text-xl font-bold text-red-700 mt-1">
                ₹{(reportData?.grand_total?.["over_90"] || 0).toLocaleString()}
              </p>
              <div className="mt-2 h-2 bg-red-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-red-500 transition-all" 
                  style={{ width: `${getAgingPercentage("over_90")}%` }}
                />
              </div>
              <p className="text-xs text-red-500 mt-1">{getAgingPercentage("over_90")}% of total</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-100 to-gray-100 dark:from-slate-800 dark:to-gray-800 border-slate-300">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-600 uppercase">Total Outstanding</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-200 mt-1">
                ₹{(reportData?.grand_total?.total || 0).toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-3">{reportData?.partner_count || 0} partners</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="no-print">
          <TabsList className="mb-4">
            <TabsTrigger value="partners">Partner-wise Aging</TabsTrigger>
            <TabsTrigger value="overdue">Overdue Orders (90+ days)</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search - Non-printable */}
        <div className="no-print mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by partner code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Partner-wise Aging Table */}
        {(activeTab === "partners" || true) && (
          <Card className={activeTab !== "partners" ? "no-print" : ""}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-amber-50 dark:bg-amber-950/30">
                      <TableHead className="w-12 font-semibold">#</TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/50"
                        onClick={() => handleSort('client_code')}
                      >
                        <div className="flex items-center">
                          Partner Code
                          <SortIcon columnKey="client_code" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-amber-100"
                        onClick={() => handleSort('client_name')}
                      >
                        <div className="flex items-center">
                          Partner Name
                          <SortIcon columnKey="client_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-amber-100 text-green-600"
                        onClick={() => handleSort('0_30')}
                      >
                        <div className="flex items-center justify-end">
                          0-30 Days
                          <SortIcon columnKey="0_30" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-amber-100 text-amber-600"
                        onClick={() => handleSort('31_60')}
                      >
                        <div className="flex items-center justify-end">
                          31-60 Days
                          <SortIcon columnKey="31_60" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-amber-100 text-orange-600"
                        onClick={() => handleSort('61_90')}
                      >
                        <div className="flex items-center justify-end">
                          61-90 Days
                          <SortIcon columnKey="61_90" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-amber-100 text-red-600"
                        onClick={() => handleSort('over_90')}
                      >
                        <div className="flex items-center justify-end">
                          90+ Days
                          <SortIcon columnKey="over_90" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-amber-100"
                        onClick={() => handleSort('total')}
                      >
                        <div className="flex items-center justify-end">
                          Total
                          <SortIcon columnKey="total" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-amber-100"
                        onClick={() => handleSort('order_count')}
                      >
                        <div className="flex items-center justify-end">
                          Orders
                          <SortIcon columnKey="order_count" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedPartners.map((partner, idx) => (
                      <TableRow 
                        key={partner.client_code} 
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${partner.over_90 > 0 ? 'bg-red-50/30 dark:bg-red-950/10' : ''}`}
                      >
                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="font-mono font-medium">{partner.client_code}</TableCell>
                        <TableCell>{partner.client_name}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {partner["0_30"] > 0 ? `₹${partner["0_30"].toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-amber-600">
                          {partner["31_60"] > 0 ? `₹${partner["31_60"].toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-orange-600">
                          {partner["61_90"] > 0 ? `₹${partner["61_90"].toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-red-600 font-medium">
                          {partner["over_90"] > 0 ? `₹${partner["over_90"].toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          ₹{partner.total.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">{partner.order_count}</TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Grand Total Row */}
                    <TableRow className="bg-amber-100 dark:bg-amber-900/50 font-bold border-t-2">
                      <TableCell colSpan={3} className="text-right">
                        GRAND TOTAL ({filteredAndSortedPartners.length} partners)
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        ₹{(reportData?.grand_total?.["0_30"] || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-amber-600">
                        ₹{(reportData?.grand_total?.["31_60"] || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-orange-600">
                        ₹{(reportData?.grand_total?.["61_90"] || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        ₹{(reportData?.grand_total?.["over_90"] || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        ₹{(reportData?.grand_total?.total || 0).toLocaleString()}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overdue Orders Tab */}
        {activeTab === "overdue" && reportData?.buckets?.over_90?.top_orders?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Top Overdue Orders (90+ Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-red-50 dark:bg-red-950/30">
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead>Tracking</TableHead>
                      <TableHead>Order Date</TableHead>
                      <TableHead>Days Old</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.buckets.over_90.top_orders.map((order, idx) => (
                      <TableRow key={idx} className="hover:bg-red-50/50">
                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{order.client_code}</TableCell>
                        <TableCell className="font-mono text-sm">{order.tracking}</TableCell>
                        <TableCell>{formatDMY(order.order_date)}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">{order.days_old} days</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{order.product}</TableCell>
                        <TableCell className="text-right font-bold text-red-600">
                          ₹{order.amount.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Print Footer */}
        <div className="hidden print:block mt-4 pt-4 border-t text-center text-xs text-gray-500">
          <p>EAC - Aging Summary Report | Page 1</p>
        </div>
      </div>
    </div>
  );
};

export default AgingSummaryReportPage;
